/**
 * BS-2151: client-side analytics emitter for OLA.
 * BS-2169: unload beacon loop + eager listener registration.
 *
 * Posts batched events to the BS-2150 ingestion endpoint
 * (`POST /api/v1/online-application-events`).
 *
 * # Design constraints
 *
 *   1. **Never break the form flow.** Analytics is a side concern; if
 *      the network is down, if the API returns 500, if the body is
 *      malformed for some reason - the form must still let the
 *      member submit. Every emit-path error is logged and swallowed.
 *
 *   2. **PII contract enforced at the boundary.** Every event passes
 *      through `filterMetadata` before going into the buffer, so even
 *      if a future refactor sneaks an extra key into `error_metadata`,
 *      it gets stripped here. The server's `.strict()` schema is the
 *      load-bearing contract; this filter is a belt-and-suspenders
 *      second line of defense.
 *
 *   3. **Batch on a 1s debounce.** The BS-2150 controller doc says a
 *      legitimate session emits at 1-2 events/sec at peak; batching
 *      coalesces field_focus + field_blur + per-keystroke validation
 *      into a single HTTP POST. Cap the batch at 50 (the server's
 *      schema cap) so a long-buffered queue can't oversize the body.
 *
 *   4. **Force-flush on tab close.** `beforeunload` + `pagehide` +
 *      `visibilitychange` to `hidden` all trigger a synchronous
 *      `navigator.sendBeacon()` flush so we don't lose the final
 *      step_abandon / submit_failure event when the user closes the
 *      tab mid-flow. Listeners register at module load (BS-2169) so
 *      pages that navigate away before any emit are still covered.
 *      The beacon flush loops until the buffer is empty (BS-2169) so
 *      buffers with >50 events are fully drained via multiple beacons.
 *
 *   5. **Drop, don't block, on backpressure.** If the in-memory
 *      buffer somehow grows past `MAX_BUFFER_SIZE` (200 events - 4x
 *      a single batch), we drop the oldest events. The alternative
 *      (block, queue indefinitely, eventually OOM) is worse for the
 *      form flow than dropping analytics signals.
 */

// BS-2161: `import type` (not `import { type ... }`) so Turbopack erases the
// statement at compile time. The mixed-form import was pulling
// @multiplicu/contracts/los's full barrel - dozens of zod schemas - into the
// shared route bundle even though only the names above are referenced (and
// only as types).
import type {
  OnlineApplicationEvent,
  OnlineApplicationEventType,
  OnlineApplicationStep,
  ErrorMetadata,
  SessionLinkedPayload,
} from '@multiplicu/contracts/los';

import { getSessionId } from './session';
import { filterMetadata } from './sanitize';

// ---------------------------------------------------------------------------
// Public emit types
// ---------------------------------------------------------------------------

/**
 * Optional fields callers may supply when emitting an event. The
 * required `event_type` and `step` are separate arguments to
 * `emitEvent` so misuse (omitting them) is a type error rather than a
 * silently-rejected runtime error.
 */
export type EmitEventOptions = {
  field_name?: string;
  error_code?: string;
  /**
   * Sanitized metadata. Callers SHOULD compute this via
   * `sanitizeFieldValue(rawValue)` - never pass a raw value here.
   * Any non-allow-listed key is stripped before the network call.
   */
  error_metadata?: ErrorMetadata | Record<string, unknown>;
  loan_type?: string;
  viewport_width?: number;
};

// ---------------------------------------------------------------------------
// Buffer state (module-scoped singleton)
// ---------------------------------------------------------------------------

/**
 * The 1-second debounce window. Larger windows reduce HTTP overhead
 * but increase the chance of losing buffered events on tab close;
 * 1s is the BS-2151 ticket's recommendation and matches typical
 * step-transition cadence (member spends much longer than 1s on a
 * form step).
 */
const FLUSH_DEBOUNCE_MS = 1_000;

/**
 * The BS-2150 schema rejects batches over 50. We trim at 50 here so
 * a long-paused tab that wakes up with a flooded buffer doesn't
 * trigger a 400 cascade. Events that don't fit go on the front of
 * the next flush.
 */
const MAX_BATCH_SIZE = 50;

/**
 * Hard cap on in-memory buffer size. Beyond this, we drop the oldest
 * events. Sized at 4x MAX_BATCH_SIZE so a temporary network blip
 * (4s of buffered emits at peak rate) is absorbed without loss, but
 * a sustained outage doesn't grow unbounded.
 */
const MAX_BUFFER_SIZE = 200;

/**
 * Explicit upper bound for the `flushViaBeacon` drain loop.
 *
 * The implicit bound today is MAX_BUFFER_SIZE / MAX_BATCH_SIZE = 200 / 50
 * = 4 iterations. We set this constant to 10 (2.5x headroom) so the loop
 * is safe even if MAX_BUFFER_SIZE or MAX_BATCH_SIZE are adjusted without
 * re-auditing the loop. Without an explicit cap, a future invariant drift
 * (e.g. MAX_BATCH_SIZE shrinks to 10) would silently produce a 20-iteration
 * synchronous loop on tab close - undesirable in the page-unload path.
 *
 * BS-2173: added to make the loop bound load-bearing and self-documenting.
 */
const MAX_LOOP_ITERATIONS = 10;

let buffer: OnlineApplicationEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let unloadListenersAttached = false;

// ---------------------------------------------------------------------------
// Endpoint resolution
// ---------------------------------------------------------------------------

/**
 * The OLA API base, mirroring the convention used by
 * `application-helper.ts` et al. We resolve at call time (not module
 * init) so test environments that set `NEXT_PUBLIC_LOAN_APP_API` after
 * import still pick it up.
 *
 * Returns `null` (not undefined or empty string) so callers can use a
 * single truthy-check and short-circuit the network call cleanly.
 */
function getEndpoint(): string | null {
  const base = process.env.NEXT_PUBLIC_LOAN_APP_API?.replace(/\/$/, '');
  if (!base) return null;
  return `${base}/api/v1/online-application-events`;
}

// ---------------------------------------------------------------------------
// Logging - Sentry-aware, but degrades to console.warn
// ---------------------------------------------------------------------------

/**
 * Log an analytics-pipeline failure without breaking the form flow.
 * We deliberately import Sentry lazily so the analytics module
 * doesn't pull Sentry into bundles that wouldn't otherwise need it,
 * and so unit tests don't need to stub Sentry just to exercise the
 * emit path.
 *
 * BS-2146 wired Sentry with PII scrubbing; the analytics module is
 * a downstream consumer.
 */
async function logFailure(error: unknown, context: string): Promise<void> {
  try {
    const sentry = await import('@sentry/nextjs');
    sentry.captureException(error, {
      tags: { module: 'ola-analytics', context },
    });
  } catch {
    // Sentry import failed (e.g. test env). Fall back to console so
    // a developer running locally sees the signal.
    // eslint-disable-next-line no-console
    console.warn(`[ola-analytics] ${context}:`, error);
  }
}

// ---------------------------------------------------------------------------
// Buffer management
// ---------------------------------------------------------------------------

/**
 * Push an event into the buffer, applying the size cap. If the buffer
 * is already at MAX_BUFFER_SIZE, drop the oldest event to make room ,
 * recent events are more useful for funnel analysis than ancient
 * ones, and we'd rather drop a focus event than a step_complete.
 */
function pushToBuffer(event: OnlineApplicationEvent): void {
  if (buffer.length >= MAX_BUFFER_SIZE) {
    buffer.shift();
  }
  buffer.push(event);
}

/**
 * Drain up to MAX_BATCH_SIZE events off the front of the buffer.
 * Leaves the remainder in place so they're picked up on the next
 * flush. Returns an empty array when the buffer is empty.
 */
function drainBatch(): OnlineApplicationEvent[] {
  if (buffer.length === 0) return [];
  const batch = buffer.slice(0, MAX_BATCH_SIZE);
  buffer = buffer.slice(MAX_BATCH_SIZE);
  return batch;
}

/**
 * Restore events to the front of the buffer (used when a flush
 * fails and we want the next flush attempt to retry them). Respects
 * the MAX_BUFFER_SIZE cap - if restoring would overflow, we keep the
 * newer events that accumulated while the in-flight POST was running,
 * because those are the ones the next flush is about to send anyway.
 */
function restoreBatch(events: OnlineApplicationEvent[]): void {
  const room = MAX_BUFFER_SIZE - buffer.length;
  if (room <= 0) return;
  const keep = events.slice(-room);
  buffer = [...keep, ...buffer];
}

// ---------------------------------------------------------------------------
// Flush - fetch path + sendBeacon path
// ---------------------------------------------------------------------------

type BatchPayload = {
  session_id: string;
  events: OnlineApplicationEvent[];
};

/**
 * Build the batch envelope. Hoists `session_id` per the BS-2150
 * schema (events themselves don't carry it).
 */
function buildPayload(events: OnlineApplicationEvent[]): BatchPayload {
  return {
    session_id: getSessionId(),
    events,
  };
}

/**
 * Async flush via `fetch`. Used for the normal 1s-debounced flush
 * path. Errors are logged and the batch is re-queued so the next
 * flush retries.
 *
 * Returns `true` if the POST succeeded (or if there was nothing to
 * send), `false` if it failed. Callers (like `flushNow`) can use the
 * result to decide whether to fall back to sendBeacon.
 */
async function flushViaFetch(): Promise<boolean> {
  const batch = drainBatch();
  if (batch.length === 0) return true;

  const endpoint = getEndpoint();
  if (!endpoint) {
    // No API base configured - silently drop. This happens in test
    // environments and in unconfigured dev setups; it's not a failure
    // state worth Sentry noise.
    return true;
  }

  const payload = buildPayload(batch);
  if (!payload.session_id) {
    // SSR or storage failure - re-queue and try again on the next
    // emit (which will run client-side and have a session_id).
    restoreBatch(batch);
    return false;
  }

  // BS-2176: AbortController with a 2-second timeout. Every caller of
  // flushViaFetch (including the BS-2176 race-fix `await flushNow()`
  // before `emitSessionLinked`) must be bounded so a stalled analytics
  // endpoint cannot hang the form flow. The browser's default fetch
  // timeout is minutes (or never, for some configurations); without an
  // explicit bound the "never block the form" contract is violated.
  // On timeout we re-queue the batch and treat it like any other
  // network failure so the next flush retries.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2000);

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // The endpoint is intentionally not session-authenticated
      // (BS-2150), so we don't need credentials. Omitting them also
      // avoids a pre-flight on cross-origin previews.
      credentials: 'omit',
      keepalive: true,
      signal: controller.signal,
    });

    if (!response.ok) {
      // 4xx responses are likely a schema regression - don't re-queue
      // (would loop forever). 5xx is probably transient - re-queue and
      // try the next flush.
      if (response.status >= 500) {
        restoreBatch(batch);
      }
      await logFailure(new Error(`HTTP ${response.status} from analytics endpoint`), 'flush');
      return false;
    }

    return true;
  } catch (error) {
    // Network error OR aborted by our 2-second timeout. Re-queue for
    // the next flush attempt either way. The catch path is the
    // load-bearing piece of the BS-2176 form-flow bound: even if the
    // analytics endpoint hangs indefinitely, we resolve in <=2 seconds.
    restoreBatch(batch);
    const tag =
      error instanceof Error && error.name === 'AbortError' ? 'flush-timeout' : 'flush-network';
    await logFailure(error, tag);
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Synchronous flush via `navigator.sendBeacon`. Used on tab close
 * paths where an async fetch would be cancelled before completion.
 *
 * sendBeacon returns `true` if the browser successfully queued the
 * request for transmission; that does NOT mean the server received
 * it, just that the browser took ownership. For our use case (last
 * gasp on tab close) that's the best we can do.
 *
 * BS-2169: loops until the buffer is empty so buffers with >50 events
 * are fully drained via multiple beacon calls rather than silently
 * dropping events past the first batch. The beacon spec allows
 * multiple in-flight beacons; payload size is per-call.
 *
 * Quirk: sendBeacon has a payload size cap (varies by browser, often
 * 64KB). Our batches are small (< 50 events, each a few hundred
 * bytes) so we shouldn't hit it, but if we do, sendBeacon returns
 * false and we stop looping - the alternative is a synchronous XHR
 * which freezes the tab close and gets blocked by modern browsers.
 * Acceptable loss.
 */
function flushViaBeacon(): boolean {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
  if (buffer.length === 0) return true;

  const endpoint = getEndpoint();
  if (!endpoint) return true;

  // BS-2169: loop until the buffer is drained, sending one beacon per
  // MAX_BATCH_SIZE chunk. Stop immediately if any beacon call fails so
  // we don't repeatedly bounce against a size cap.
  //
  // BS-2173: explicit iteration cap (MAX_LOOP_ITERATIONS = 10) so future
  // invariant drift on MAX_BUFFER_SIZE / MAX_BATCH_SIZE cannot accidentally
  // make this a runaway synchronous loop on the page-unload path.
  // The implicit bound today is MAX_BUFFER_SIZE / MAX_BATCH_SIZE = 4.
  let allQueued = true;
  for (let i = 0; i < MAX_LOOP_ITERATIONS && buffer.length > 0; i++) {
    const batch = drainBatch();
    if (batch.length === 0) break;

    const payload = buildPayload(batch);
    if (!payload.session_id) {
      restoreBatch(batch);
      return false;
    }

    try {
      // Browsers require a Blob with a content-type for the request to
      // hit CORS preflight cleanly. `application/json` matches the
      // server-side expectation.
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      });
      const queued = navigator.sendBeacon(endpoint, blob);
      if (!queued) {
        // Browser rejected the beacon (size cap, disabled, etc.).
        // Restore this batch and stop looping - remaining events in the
        // buffer are also unlikely to be accepted. In practice the tab
        // is about to close; this is a best-effort path.
        restoreBatch(batch);
        allQueued = false;
        break;
      }
    } catch {
      // sendBeacon shouldn't throw, but if it does, restore the batch
      // and stop looping.
      restoreBatch(batch);
      allQueued = false;
      break;
    }
  }
  return allQueued;
}

// ---------------------------------------------------------------------------
// Debounce timer
// ---------------------------------------------------------------------------

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushViaFetch();
  }, FLUSH_DEBOUNCE_MS);
}

function cancelScheduledFlush(): void {
  if (flushTimer === null) return;
  clearTimeout(flushTimer);
  flushTimer = null;
}

// ---------------------------------------------------------------------------
// Unload handlers - install eagerly at module load (BS-2169)
// ---------------------------------------------------------------------------

function handleUnload(): void {
  // We don't await here - the tab is going away. sendBeacon is the
  // only flush path with a chance of completing.
  cancelScheduledFlush();
  flushViaBeacon();
}

/**
 * Named visibilitychange handler so `removeEventListener` can target it
 * by reference. An anonymous arrow passed to `addEventListener` cannot be
 * removed - the reference is lost - which caused duplicate listener
 * stacking across `__resetAnalyticsForTesting()` calls in long Vitest runs.
 *
 * BS-2173: hoisted from the anonymous arrow that was inline in
 * `attachUnloadListeners` so the test helper can cleanly remove it.
 */
function handleVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    handleUnload();
  }
}

/**
 * Register the three unload listeners. Called once at module load
 * (BS-2169) so pages that mount and navigate away before any emit
 * are still covered. The guard flag prevents double-registration if
 * the module is somehow evaluated twice.
 *
 * Previous behaviour registered lazily inside `emitEvent`; that left
 * a gap where a page mount followed by an immediate navigation (fast
 * redirect, crash) would miss the listener and lose buffered events.
 *
 * BS-2173: all three listeners are now hoisted named functions so
 * `__removeUnloadListenersForTesting` can remove them by reference,
 * preventing listener stacking across test resets.
 */
function attachUnloadListeners(): void {
  if (unloadListenersAttached) return;
  if (typeof window === 'undefined') return;

  // `pagehide` is the modern replacement for `beforeunload` for the
  // "tab is going away" event. Most browsers fire both; some mobile
  // browsers only fire pagehide. visibilitychange to hidden catches
  // the case where the user backgrounds the tab (iOS Safari often
  // skips pagehide for backgrounded tabs).
  window.addEventListener('pagehide', handleUnload);
  window.addEventListener('beforeunload', handleUnload);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  unloadListenersAttached = true;
}

// BS-2169: register immediately on module load so pages that navigate
// away before any emitEvent call are still covered. No-op in SSR
// (window guard inside attachUnloadListeners).
attachUnloadListeners();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * BS-2152: endpoint for the `session_linked` event. Resolved lazily
 * (same pattern as `getEndpoint`) so test environments that set the
 * env var after import still pick it up.
 */
function getSessionLinkEndpoint(): string | null {
  const base = process.env.NEXT_PUBLIC_LOAN_APP_API?.replace(/\/$/, '');
  if (!base) return null;
  return `${base}/api/v1/online-application-events/session-link`;
}

/**
 * BS-2152: fire-and-forget POST to the session-link endpoint.
 *
 * Called immediately after a successful member login so the API can
 * back-fill `linked_application_id` + `linked_at` on the session row.
 *
 * Design constraints mirror `emitEvent`:
 *   - Never throws - analytics must not break the form flow.
 *   - Does NOT go through the event buffer; the link event is a
 *     dedicated one-shot call, not a funnel telemetry tick.
 *   - Retried once on 5xx, silently dropped on 4xx.
 *
 * @param memberId - The member's credit-union account number.
 * @param applicationId - Optional application UUID if one already
 *   exists at link time (rare; most logins precede app creation).
 */
export async function emitSessionLinked(memberId: string, applicationId?: string): Promise<void> {
  try {
    const endpoint = getSessionLinkEndpoint();
    if (!endpoint) return;

    const sessionId = getSessionId();
    if (!sessionId) return;

    const payload: SessionLinkedPayload = {
      session_id: sessionId,
      member_id: memberId,
      ...(applicationId ? { application_id: applicationId } : {}),
    };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit',
      keepalive: true,
    });

    if (!response.ok && response.status >= 500) {
      // Single retry on transient server error.
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
      });
    }
  } catch (error) {
    // Non-throwing per the analytics never-break-the-form contract.
    void logFailure(error, 'session-link');
  }
}

/**
 * Emit a single analytics event into the buffered queue. Returns
 * immediately; the actual network call happens on a 1s debounce or
 * on tab-close beacon.
 *
 * The event_type and step are typed against the canonical contract
 * enums so a typo (e.g. `step_views` instead of `step_view`) is a
 * compile error.
 *
 * Failures within this function (sanitization, buffer push, anything)
 * are caught and logged - analytics must not break the form flow.
 */
export function emitEvent(
  event_type: OnlineApplicationEventType,
  step: OnlineApplicationStep,
  options: EmitEventOptions = {}
): void {
  try {
    const sanitizedMetadata = filterMetadata(
      options.error_metadata as Record<string, unknown> | undefined
    );

    const event: OnlineApplicationEvent = {
      event_type,
      step,
      ...(options.field_name ? { field_name: options.field_name } : {}),
      ...(options.error_code ? { error_code: options.error_code } : {}),
      ...(sanitizedMetadata ? { error_metadata: sanitizedMetadata } : {}),
      ...(options.loan_type ? { loan_type: options.loan_type } : {}),
      ...(typeof options.viewport_width === 'number'
        ? { viewport_width: options.viewport_width }
        : {}),
    };

    pushToBuffer(event);
    scheduleFlush();
  } catch (error) {
    // Catch absolutely everything - a programming error in this
    // module must not crash the form. We log via the async pathway
    // but don't await it; the void cast is intentional.
    void logFailure(error, 'emit');
  }
}

/**
 * Force-flush the buffer immediately via fetch. Returns a promise
 * that resolves when the flush completes (success or failure).
 *
 * Use cases:
 *   - Tests asserting on the emitted batch shape.
 *   - Explicit "send now" calls before navigations the framework
 *     might unmount the listener before the debounce fires.
 *
 * For the tab-close path, prefer the auto-installed unload listeners
 * (they use sendBeacon, which actually completes when the tab is
 * about to close - a fetch would be cancelled).
 */
export async function flushNow(): Promise<void> {
  try {
    cancelScheduledFlush();
    await flushViaFetch();
  } catch (error) {
    await logFailure(error, 'flushNow');
  }
}

/**
 * Force-flush via sendBeacon. Synchronous. Returns true if the
 * browser queued the request, false otherwise (or if there was
 * nothing to send, which also returns true).
 *
 * Exposed for explicit "tab is closing" call sites that want to
 * trigger the beacon path without waiting for the unload event.
 */
export function flushBeacon(): boolean {
  try {
    cancelScheduledFlush();
    return flushViaBeacon();
  } catch (error) {
    void logFailure(error, 'flushBeacon');
    return false;
  }
}

/**
 * Test-only: remove the unload listeners that were attached by
 * `attachUnloadListeners`. Because all three handlers are now named
 * functions (BS-2173), `removeEventListener` can target them by
 * reference and actually detach them rather than silently no-oping
 * (which is what happens when you try to remove an anonymous arrow).
 *
 * Call this before `__resetAnalyticsForTesting` (or let the reset
 * helper call it automatically) to prevent duplicate-listener
 * stacking in long Vitest runs where the module is re-imported or
 * reset many times.
 *
 * Not exported from the package index - tests only.
 */
export function __removeUnloadListenersForTesting(): void {
  if (typeof window === 'undefined') return;
  window.removeEventListener('pagehide', handleUnload);
  window.removeEventListener('beforeunload', handleUnload);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * Test-only: reset the module's internal state. Not exported from
 * the package index - only the test files in this directory import
 * it. Calling this in production code is a sign of misuse.
 *
 * BS-2173: now calls `__removeUnloadListenersForTesting` before
 * flipping `unloadListenersAttached = false` so that the subsequent
 * `attachUnloadListeners()` call re-attaches exactly one copy of each
 * listener - preventing listener stacking in long Vitest runs where
 * this helper is called many times.
 *
 * After removing and clearing the flag, `attachUnloadListeners()` is
 * called to re-register a fresh listener set, mirroring the module-load
 * behaviour. This means tests always run with exactly one listener
 * registered per event type, regardless of how many times reset was
 * called before them.
 */
export function __resetAnalyticsForTesting(): void {
  buffer = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  __removeUnloadListenersForTesting();
  unloadListenersAttached = false;
  // Re-attach a fresh set of listeners so the next test runs with exactly
  // one listener per event type - matching the real module-load state.
  attachUnloadListeners();
}

/**
 * Test-only: read the current buffer contents (a copy, not the
 * reference). Used to assert batching behavior in unit tests.
 */
export function __getBufferForTesting(): OnlineApplicationEvent[] {
  return [...buffer];
}
